import request from '@/utils/request'
import { CONVERT_COORDINATES_ENDPOINT } from './types'

/**
 * Stateless GIS conversion. Uses the shared request client (auth, GPS, locale).
 * Never call POST /projects/convertCoordinates from this module.
 *
 * @param {import('./types').ConvertCoordinatesJsonRequest} payload
 * @returns {Promise<import('./types').ConvertCoordinatesResponse>}
 */
export function convertCoordinatesJson(payload) {
  return request({
    url: CONVERT_COORDINATES_ENDPOINT,
    method: 'post',
    data: payload,
  })
}

/**
 * File-only multipart request. Do not attach a `coordinates` field.
 *
 * @param {{ file: File, source_system?: string, utm_zone?: string }} payload
 * @returns {Promise<import('./types').ConvertCoordinatesResponse>}
 */
export function convertCoordinatesFile(payload) {
  const formData = new FormData()
  formData.append('file', payload.file)

  if (payload.source_system) {
    formData.append('source_system', payload.source_system)
  }

  if (payload.utm_zone) {
    formData.append('utm_zone', payload.utm_zone)
  }

  return request({
    url: CONVERT_COORDINATES_ENDPOINT,
    method: 'post',
    data: formData,
  })
}

/**
 * @param {unknown} error
 * @returns {{ message: string, errors: string[], status: number|null }}
 */
export function extractConvertCoordinatesError(error) {
  const response = error && error.response
  const status = response && response.status ? Number(response.status) : null
  const body = response && response.data ? response.data : null

  if (!body) {
    return {
      message: (error && error.message) || '',
      errors: [],
      status,
    }
  }

  const message = typeof body.message === 'string' ? body.message : ''
  const errors = []

  if (Array.isArray(body.errors)) {
    body.errors.forEach(item => {
      if (typeof item === 'string' && item) errors.push(item)
    })
  } else if (body.errors && typeof body.errors === 'object') {
    Object.keys(body.errors).forEach(key => {
      const value = body.errors[key]
      if (Array.isArray(value)) {
        value.forEach(item => {
          if (item) errors.push(String(item))
        })
      } else if (value) {
        errors.push(String(value))
      }
    })
  } else if (typeof body.errors === 'string' && body.errors) {
    errors.push(body.errors)
  }

  return { message, errors, status }
}
